import API_CONFIG from "./apiConfig";

// Token storage keys
const TOKEN_KEY = "gymnote_token";
const USER_KEY = "gymnote_user";

/**
 * Make API request with proper headers
 */
const apiRequest = async (endpoint, options = {}) => {
	const url = `${API_CONFIG.BASE_URL}${endpoint}`;

	const headers = {
		"Content-Type": "application/json",
		...options.headers,
	};

	const response = await fetch(url, {
		...options,
		headers,
	});

	return response;
};

/**
 * Login with email and password
 * @returns {Promise<{success: boolean, user?: object, error?: string}>}
 */
export const login = async (email, password) => {
	try {
		const response = await apiRequest("/compte/connexion", {
			method: "POST",
			body: JSON.stringify({
				Email: email,
				Mdp: password,
			}),
		});

		if (response.ok) {
			const token = await response.text();

			if (token && token.length > 0) {
				// Store token
				localStorage.setItem(TOKEN_KEY, token);

				// Get user info
				const userInfo = await getUserInfo(token);

				if (userInfo) {
					localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
					return { success: true, user: userInfo };
				}

				return { success: true, user: null };
			}
		}

		// Get error details from response
		const errorText = await response.text();
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Email ou mot de passe incorrect"}` };
	} catch (error) {
		console.error("Login error:", error);
		return { success: false, error: `Erreur de connexion: ${error.message}` };
	}
};

/**
 * Get user info from token
 */
export const getUserInfo = async (token = null) => {
	try {
		const authToken = token || getToken();

		if (!authToken) {
			return null;
		}

		const response = await apiRequest("/compte/getInfoUser", {
			method: "POST",
			body: JSON.stringify({
				Token: authToken,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return {
				id: data.userId,
				prenom: data.prenom || "",
				nom: data.nom || "",
				email: data.email || "",
				emailVerifie: data.emailVerifie || false,
				abonnement: data.abonnement || null,
				photoProfil: data.photoProfil || null,
			};
		}

		return null;
	} catch (error) {
		console.error("Get user info error:", error);
		return null;
	}
};

/**
 * Login with Google ID token
 * @returns {Promise<{success: boolean, user?: object, isNewAccount?: boolean, error?: string}>}
 */
export const loginWithGoogle = async (idToken) => {
	try {
		const response = await apiRequest("/compte/google-login", {
			method: "POST",
			body: JSON.stringify({
				IdToken: idToken,
			}),
		});

		if (response.ok) {
			const data = await response.json();

			if (data.token && data.token.length > 0) {
				localStorage.setItem(TOKEN_KEY, data.token);

				const userInfo = await getUserInfo(data.token);

				if (userInfo) {
					localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
					return { success: true, user: userInfo, isNewAccount: data.isNewAccount };
				}

				return { success: true, user: null, isNewAccount: data.isNewAccount };
			}
		}

		const errorText = await response.text();
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Échec de la connexion Google"}` };
	} catch (error) {
		console.error("Google login error:", error);
		return { success: false, error: `Erreur de connexion: ${error.message}` };
	}
};

/**
 * Login with Apple identity token
 * @returns {Promise<{success: boolean, user?: object, isNewAccount?: boolean, error?: string}>}
 */
export const loginWithApple = async (identityToken, fullName, email) => {
	try {
		const response = await apiRequest("/compte/apple-login", {
			method: "POST",
			body: JSON.stringify({
				IdentityToken: identityToken,
				FullName: fullName,
				Email: email,
			}),
		});

		if (response.ok) {
			const data = await response.json();

			if (data.token && data.token.length > 0) {
				localStorage.setItem(TOKEN_KEY, data.token);

				const userInfo = await getUserInfo(data.token);

				if (userInfo) {
					localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
					return { success: true, user: userInfo, isNewAccount: data.isNewAccount };
				}

				return { success: true, user: null, isNewAccount: data.isNewAccount };
			}
		}

		const errorText = await response.text();
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Échec de la connexion Apple"}` };
	} catch (error) {
		console.error("Apple login error:", error);
		return { success: false, error: `Erreur de connexion: ${error.message}` };
	}
};

export const logout = () => {
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(USER_KEY);
};

/**
 * Get stored token
 */
export const getToken = () => {
	return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get stored user
 */
export const getUser = () => {
	const userStr = localStorage.getItem(USER_KEY);
	if (userStr) {
		try {
			return JSON.parse(userStr);
		} catch {
			return null;
		}
	}
	return null;
};

/**
 * Check if user is logged in
 */
export const isLoggedIn = () => {
	return !!getToken();
};

/**
 * Verify if stored token is still valid
 */
export const verifySession = async () => {
	const token = getToken();

	if (!token) {
		return false;
	}

	const userInfo = await getUserInfo(token);

	if (userInfo) {
		localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
		return true;
	}

	// Token invalid, clear storage
	logout();
	return false;
};

/**
 * Update user profile (prenom/nom)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateProfile = async (prenom, nom) => {
	try {
		const token = getToken();
		const user = getUser();

		if (!token || !user) {
			return { success: false, error: "Non connecté" };
		}

		const response = await apiRequest("/compte/modifier-profil", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				Email: user.email,
				Prenom: prenom,
				Nom: nom,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		return { success: false, error: "Erreur lors de la mise à jour du profil" };
	} catch (error) {
		console.error("Update profile error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get user profile stats
 * @returns {Promise<{success: boolean, stats?: object, error?: string}>}
 */
export const getProfileStats = async () => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecté" };
		}

		const response = await apiRequest("/user/profile-stats", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, stats: data };
		}

		return { success: false, error: "Erreur lors de la récupération des statistiques" };
	} catch (error) {
		console.error("Get profile stats error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Request password reset
 */
export const requestPasswordReset = async (email) => {
	try {
		const response = await apiRequest("/auth/password-reset/request", {
			method: "POST",
			body: JSON.stringify({
				Email: email,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		return { success: false, error: "Erreur lors de l'envoi du code" };
	} catch (error) {
		console.error("Password reset request error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Verify password reset code
 */
export const verifyResetCode = async (email, code) => {
	try {
		const response = await apiRequest("/auth/password-reset/verify", {
			method: "POST",
			body: JSON.stringify({
				Email: email,
				Code: code,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		return { success: false, error: "Code invalide ou expiré" };
	} catch (error) {
		console.error("Verify reset code error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Change password with reset code
 */
export const changePassword = async (email, code, newPassword) => {
	try {
		const response = await apiRequest("/auth/password-reset/change", {
			method: "POST",
			body: JSON.stringify({
				Email: email,
				Code: code,
				NewPassword: newPassword,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		return { success: false, error: "Erreur lors du changement de mot de passe" };
	} catch (error) {
		console.error("Change password error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Resend email confirmation
 */
export const resendConfirmationEmail = async (email) => {
	try {
		const response = await apiRequest("/auth/resend-confirmation", {
			method: "POST",
			body: JSON.stringify({
				Email: email,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		return { success: false, error: "Erreur lors de l'envoi de l'email" };
	} catch (error) {
		console.error("Resend confirmation error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get list of students (eleves) for the current coach
 * @returns {Promise<{success: boolean, students?: Array, error?: string}>}
 */
export const getStudents = async () => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/partner/recuperer", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
			}),
		});

		if (response.ok) {
			const partners = await response.json();
			// Filter to get only students (TypeRole = 2)
			const students = partners.filter((p) => p.typeRole === 2);
			return { success: true, students };
		}

		return { success: false, error: "Erreur lors de la recuperation des eleves" };
	} catch (error) {
		console.error("Get students error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get training days for a student in a specific month
 * @returns {Promise<{success: boolean, trainingDays?: Array<number>, error?: string}>}
 */
export const getTrainingDays = async (studentId, year, month) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/training-days", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				Year: year,
				Month: month,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, trainingDays: data.trainingDays };
		}

		return { success: false, error: "Erreur lors de la recuperation des entrainements" };
	} catch (error) {
		console.error("Get training days error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get training details for a student on a specific date
 * @returns {Promise<{success: boolean, trainings?: Array, error?: string}>}
 */
export const getTrainingDetails = async (studentId, year, month, day) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/training-details", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				Year: year,
				Month: month,
				Day: day,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, trainings: data.trainings };
		}

		return { success: false, error: "Erreur lors de la recuperation des details" };
	} catch (error) {
		console.error("Get training details error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get exercises for a student
 * @returns {Promise<{success: boolean, exercises?: Array, error?: string}>}
 */
export const getStudentExercises = async (studentId) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/student-exercises", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, exercises: data.exercises };
		}

		return { success: false, error: "Erreur lors de la recuperation des exercices" };
	} catch (error) {
		console.error("Get student exercises error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get full training data with exercises and series
 * @returns {Promise<{success: boolean, training?: object, error?: string}>}
 */
export const getTrainingFull = async (studentId, trainingId) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/training-full", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				TrainingId: trainingId,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, training: data };
		}

		return { success: false, error: "Erreur lors de la recuperation de l'entrainement" };
	} catch (error) {
		console.error("Get training full error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

export const getTrainingPreviousPerfs = async (studentId, trainingId) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/training-previous-perfs", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId, TrainingId: trainingId }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur" };
	} catch (error) {
		console.error("Get training previous perfs error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get templates for a student
 * @returns {Promise<{success: boolean, templates?: Array, error?: string}>}
 */
export const getStudentTemplates = async (studentId) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/student-templates", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, templates: data.templates };
		}

		return { success: false, error: "Erreur lors de la recuperation des templates" };
	} catch (error) {
		console.error("Get student templates error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Create a new training for a student
 * @param {number} studentId - Student ID
 * @param {string} nom - Training name
 * @param {number} date - Training date (timestamp in milliseconds)
 * @param {boolean} estTemplate - Whether this is a template
 * @param {Array} exercices - Array of exercises with IdExercice and ListSerie (each serie has reps and poids)
 * @returns {Promise<{success: boolean, trainingId?: number, error?: string}>}
 */
export const createTraining = async (studentId, nom, date, estTemplate, exercices) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/training-create", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				Nom: nom,
				Date: date,
				EstTemplate: estTemplate,
				Exercices: exercices,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, trainingId: data.trainingId };
		}

		const errorText = await response.text();
		console.error("Create training failed:", response.status, errorText);
		return { success: false, error: "Erreur lors de la creation de l'entrainement (" + response.status + ": " + errorText + ")" };
	} catch (error) {
		console.error("Create training error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Update an existing training for a student
 * @param {number} studentId - Student ID
 * @param {number} id - Training ID to update
 * @param {string} nom - Training name
 * @param {number} date - Training date (timestamp in milliseconds)
 * @param {boolean} estTemplate - Whether this is a template
 * @param {Array} exercices - Array of exercises with IdExercice and ListSerie (each serie has reps and poids)
 * @param {string} commentaire - Training comment/note
 * @returns {Promise<{success: boolean, id?: number, error?: string}>}
 */
export const updateTraining = async (studentId, id, nom, date, estTemplate, exercices, commentaire = "") => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/training-update", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				Id: id,
				Nom: nom,
				Date: date,
				EstTemplate: estTemplate,
				Exercices: exercices,
				Commentaire: commentaire,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, id: data.id };
		}

		// Get error details from response
		const errorText = await response.text();
		console.error("Update training error response:", response.status, errorText);
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Erreur lors de la modification de l'entrainement"}` };
	} catch (error) {
		console.error("Update training error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Move a training to a new date
 * @param {number} studentId - Student ID
 * @param {number} trainingId - Training ID to move
 * @param {number} newDate - New date (timestamp in milliseconds)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const moveTraining = async (studentId, trainingId, newDate) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/training-move", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				TrainingId: trainingId,
				NewDate: newDate,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		const errorText = await response.text();
		console.error("Move training error response:", response.status, errorText);
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Erreur lors du déplacement"}` };
	} catch (error) {
		console.error("Move training error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

export const deleteTraining = async (studentId, trainingId) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/training-delete", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				TrainingId: trainingId,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		const errorText = await response.text();
		console.error("Delete training error response:", response.status, errorText);
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Erreur lors de la suppression"}` };
	} catch (error) {
		console.error("Delete training error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Create a new exercise in the referentiel
 * @param {number} studentId - Student ID
 * @param {string} nomExercice - Exercise name
 * @param {string} comExercice - Exercise description
 * @param {Array<number>} muscles - Array of 0/1 values for each muscle
 * @returns {Promise<{success: boolean, id?: number, error?: string}>}
 */
export const createExercise = async (studentId, nomExercice, comExercice, muscles) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/exercise-create", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				NomExercice: nomExercice,
				ComExercice: comExercice,
				Muscles: muscles,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, id: data.id };
		}

		if (response.status === 409) {
			return { success: false, error: "Un exercice avec ce nom existe déjà" };
		}

		const errorText = await response.text();
		console.error("Create exercise error response:", response.status, errorText);
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Erreur lors de la création"}` };
	} catch (error) {
		console.error("Create exercise error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Update an exercise in the referentiel
 * @param {number} studentId - Student ID
 * @param {number} exerciseId - Exercise ID to update
 * @param {string} nomExercice - Exercise name
 * @param {string} comExercice - Exercise description
 * @param {Array<number>} muscles - Array of 0/1 values for each muscle
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateExercise = async (studentId, exerciseId, nomExercice, comExercice, muscles) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/coach/exercise-update", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				ExerciseId: exerciseId,
				NomExercice: nomExercice,
				ComExercice: comExercice,
				Muscles: muscles,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		const errorText = await response.text();
		console.error("Update exercise error response:", response.status, errorText);
		return { success: false, error: `Erreur ${response.status}: ${errorText || "Erreur lors de la modification"}` };
	} catch (error) {
		console.error("Update exercise error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

// ==================== COACH DASHBOARD ENHANCED ====================

/**
 * Get student statistics (sessions, volume, progressions, engagement)
 */
export const getStudentStats = async (studentId, period = 30) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/student-stats", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId, Period: period }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur lors de la recuperation des statistiques" };
	} catch (error) {
		console.error("Get student stats error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get exercise-specific progress over time
 */
export const getExerciseProgress = async (studentId, exerciseId, period = 90) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/exercise-progress", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId, ExerciseId: exerciseId, Period: period }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur" };
	} catch (error) {
		console.error("Get exercise progress error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get overview heatmap (all students x 30 days)
 */
export const getOverviewHeatmap = async (days = 90) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/overview-heatmap", {
			method: "POST",
			body: JSON.stringify({ Token: token, Days: days }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur" };
	} catch (error) {
		console.error("Get overview heatmap error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get coaching activity summary for current month
 */
export const getActivitySummary = async () => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/activity-summary", {
			method: "POST",
			body: JSON.stringify({ Token: token }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur" };
	} catch (error) {
		console.error("Get activity summary error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get student scores (regularity + progression) for coach overview
 */
export const getStudentScores = async (days = 90) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/student-scores", {
			method: "POST",
			body: JSON.stringify({ Token: token, Days: days }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur" };
	} catch (error) {
		console.error("Get student scores error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get coach dashboard overview (students list with alerts)
 */
export const getCoachAlerts = async () => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/dashboard-overview", {
			method: "POST",
			body: JSON.stringify({ Token: token }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur" };
	} catch (error) {
		console.error("Get coach alerts error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get coach's own templates
 */
export const getCoachTemplates = async () => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/my-templates", {
			method: "POST",
			body: JSON.stringify({ Token: token }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		return { success: false, error: "Erreur" };
	} catch (error) {
		console.error("Get coach templates error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Send template to a student
 */
export const sendTemplateToStudent = async (studentId, templateName, exercises) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/send-template", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				TemplateName: templateName,
				Exercises: exercises,
			}),
		});

		if (response.ok) return { success: true };
		return { success: false, error: "Erreur lors de l'envoi du template" };
	} catch (error) {
		console.error("Send template error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Schedule programming (planification) for a student
 */
export const scheduleProgramming = async (studentId, { templateName, exercises, templateId, dates, saveAsStudentTemplate }) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/schedule-programming", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				TemplateName: templateName,
				Exercises: exercises,
				TemplateId: templateId,
				Dates: dates,
				SaveAsStudentTemplate: saveAsStudentTemplate || false,
			}),
		});

		if (response.ok) return { success: true };
		const errorText = await response.text();
		console.error("Schedule programming failed:", response.status, errorText);
		return { success: false, error: "Erreur lors de la programmation (" + response.status + ": " + errorText + ")" };
	} catch (error) {
		console.error("Schedule programming error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Duplicate training to multiple dates
 */
export const duplicateTrainingMultiple = async (studentId, trainingId, dates) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/training-duplicate", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				StudentId: studentId,
				TrainingId: trainingId,
				Dates: dates,
			}),
		});

		if (response.ok) return { success: true };
		return { success: false, error: "Erreur lors de la duplication" };
	} catch (error) {
		console.error("Duplicate training error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

// ==================== STRIPE PAYMENT FUNCTIONS ====================

/**
 * Get all subscription types
 * @returns {Promise<{success: boolean, types?: Array, error?: string}>}
 */
export const getSubscriptionTypes = async () => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/abonnement/types/all", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, types: data };
		}

		return { success: false, error: "Erreur lors de la récupération des abonnements" };
	} catch (error) {
		console.error("Get subscription types error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get all subscription types (public, no auth required)
 * @returns {Promise<{success: boolean, types?: Array, error?: string}>}
 */
export const getSubscriptionTypesPublic = async () => {
	try {
		const response = await apiRequest("/abonnement/types/public", {
			method: "GET",
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, types: data };
		}

		return { success: false, error: "Erreur lors de la récupération des abonnements" };
	} catch (error) {
		console.error("Get public subscription types error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get current user subscription
 * @returns {Promise<{success: boolean, subscription?: object, error?: string}>}
 */
export const getCurrentSubscription = async () => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/abonnement/recuperer", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, subscription: data };
		}

		return { success: false, error: "Erreur lors de la récupération de l'abonnement" };
	} catch (error) {
		console.error("Get current subscription error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Create a Stripe subscription and get client secret for payment
 * @param {number} abonnementTypeId - Subscription plan ID
 * @returns {Promise<{success: boolean, clientSecret?: string, subscriptionId?: string, error?: string}>}
 */
export const createStripeSubscription = async (abonnementTypeId) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/stripe/create-subscription", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				AbonnementTypeId: abonnementTypeId,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return {
				success: true,
				clientSecret: data.clientSecret,
				subscriptionId: data.subscriptionId,
			};
		}

		const errorText = await response.text();
		console.error("Create subscription error:", response.status, errorText);
		return { success: false, error: errorText || "Erreur lors de la création de l'abonnement" };
	} catch (error) {
		console.error("Create Stripe subscription error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Cancel current Stripe subscription
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const cancelStripeSubscription = async () => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/stripe/cancel-subscription", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		const errorText = await response.text();
		console.error("Cancel subscription error:", response.status, errorText);
		return { success: false, error: errorText || "Erreur lors de l'annulation" };
	} catch (error) {
		console.error("Cancel Stripe subscription error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get unread message count for the current user
 * @returns {Promise<{success: boolean, unreadCount?: number, error?: string}>}
 */
export const getUnreadMessageCount = async () => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/message/unread-count", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, unreadCount: data.unreadCount };
		}

		return { success: false, error: "Erreur lors de la récupération des messages non lus" };
	} catch (error) {
		console.error("Get unread message count error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get all conversations for the current user
 * @returns {Promise<{success: boolean, conversations?: Array, error?: string}>}
 */
export const getConversations = async () => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/message/conversations", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
			}),
		});

		if (response.ok) {
			const conversations = await response.json();
			return { success: true, conversations };
		}

		return { success: false, error: "Erreur lors de la récupération des conversations" };
	} catch (error) {
		console.error("Get conversations error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get messages for a specific conversation
 * @param {number} partnerId - Partner user ID
 * @param {number} page - Page number (default 1)
 * @param {number} pageSize - Messages per page (default 30)
 * @returns {Promise<{success: boolean, messages?: Array, hasMore?: boolean, error?: string}>}
 */
export const getConversation = async (partnerId, page = 1, pageSize = 30) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/message/conversation", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				PartnerId: partnerId,
				Page: page,
				PageSize: pageSize,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, messages: data.messages, hasMore: data.hasMore };
		}

		return { success: false, error: "Erreur lors de la récupération de la conversation" };
	} catch (error) {
		console.error("Get conversation error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Send a message to a user
 * @param {number} receiverId - Receiver user ID
 * @param {string} content - Message content
 * @returns {Promise<{success: boolean, idMessage?: number, error?: string}>}
 */
export const sendMessage = async (receiverId, content) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/message/send", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				IdReceiver: receiverId,
				Contenu: content,
			}),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, idMessage: data.idMessage };
		}

		return { success: false, error: "Erreur lors de l'envoi du message" };
	} catch (error) {
		console.error("Send message error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Mark all messages from a partner as read
 * @param {number} partnerId - Partner user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const markMessagesAsRead = async (partnerId) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/message/read", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				PartnerId: partnerId,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		return { success: false, error: "Erreur lors du marquage des messages" };
	} catch (error) {
		console.error("Mark messages as read error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get messages for a group conversation
 * @returns {Promise<{success: boolean, messages?: Array, hasMore?: boolean, error?: string}>}
 */
export const getGroupConversation = async (idGroup, page = 1, pageSize = 30) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/group/conversation", {
			method: "POST",
			body: JSON.stringify({ Token: token, IdGroup: idGroup, Page: page, PageSize: pageSize }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, messages: data.messages, hasMore: data.hasMore };
		}
		return { success: false, error: "Erreur lors de la récupération de la conversation de groupe" };
	} catch (error) {
		console.error("Get group conversation error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Send a message to a group
 * @returns {Promise<{success: boolean, idMessage?: number, error?: string}>}
 */
export const sendGroupMessage = async (idGroup, content) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/group/send", {
			method: "POST",
			body: JSON.stringify({ Token: token, IdGroup: idGroup, Contenu: content }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, idMessage: data.idMessage };
		}
		return { success: false, error: "Erreur lors de l'envoi du message de groupe" };
	} catch (error) {
		console.error("Send group message error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Mark all messages of a group as read
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const markGroupMessagesAsRead = async (idGroup) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/group/read", {
			method: "POST",
			body: JSON.stringify({ Token: token, IdGroup: idGroup }),
		});

		if (response.ok) return { success: true };
		return { success: false, error: "Erreur lors du marquage des messages de groupe" };
	} catch (error) {
		console.error("Mark group messages as read error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Schedule a plan change for next billing cycle
 * @param {number} newAbonnementTypeId - New subscription plan ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const schedulePlanChange = async (newAbonnementTypeId) => {
	try {
		const token = getToken();

		if (!token) {
			return { success: false, error: "Non connecte" };
		}

		const response = await apiRequest("/stripe/schedule-plan-change", {
			method: "POST",
			body: JSON.stringify({
				Token: token,
				NewAbonnementTypeId: newAbonnementTypeId,
			}),
		});

		if (response.ok) {
			return { success: true };
		}

		const errorText = await response.text();
		console.error("Schedule plan change error:", response.status, errorText);
		return { success: false, error: errorText || "Erreur lors du changement de plan" };
	} catch (error) {
		console.error("Schedule plan change error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get coach private note and weekly goal for a student
 * @param {number} studentId - Student ID
 * @returns {Promise<{success: boolean, note?: string, goalSessionsPerWeek?: number, error?: string}>}
 */
export const getStudentNote = async (studentId) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/student-note", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, note: data.note ?? "", goalSessionsPerWeek: data.goalSessionsPerWeek ?? 0 };
		}
		return { success: false, error: "Erreur lors de la récupération de la note" };
	} catch (error) {
		console.error("Get student note error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Save coach private note and weekly goal for a student
 * @param {number} studentId - Student ID
 * @param {string} note - Private coach note
 * @param {number} goalSessionsPerWeek - Weekly sessions goal (0 = disabled)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const saveStudentNote = async (studentId, note, goalSessionsPerWeek) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecte" };

		const response = await apiRequest("/coach/student-note/save", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId, Note: note, GoalSessionsPerWeek: goalSessionsPerWeek }),
		});

		if (response.ok) return { success: true };
		return { success: false, error: "Erreur lors de la sauvegarde de la note" };
	} catch (error) {
		console.error("Save student note error:", error);
		return { success: false, error: "Erreur de connexion au serveur" };
	}
};

/**
 * Get exercise overview for a student over a given period
 * @param {number} studentId - Student ID
 * @param {number} period - Number of days to look back (default 30)
 * @returns {Promise<{success: boolean, exercises?: Array}>}
 */
export const getStudentExerciseOverview = async (studentId, period = 30) => {
	try {
		const token = getToken();
		if (!token) return { success: false };
		const response = await apiRequest("/coach/student-exercise-overview", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId, Period: period }),
		});
		if (response.ok) {
			const data = await response.json();
			return { success: true, exercises: data.exercises || [] };
		}
		return { success: false };
	} catch (e) {
		return { success: false };
	}
};

/**
 * Get weekly report for all students
 * @param {number} weekOffset - How many weeks back (0 = current, 1 = last week)
 * @returns {Promise<{success: boolean, data?: object}>}
 */
/**
 * For a list of referentiel exercise ids, returns the series of the LAST session
 * (non-template, non-deleted) of the student where each exercise was performed.
 * Used to pre-fill weights when loading a template.
 */
export const getLastExerciseSeries = async (studentId, exerciseIds) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecté" };
		const response = await apiRequest("/coach/last-exercise-series", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId, ExerciseIds: exerciseIds }),
		});
		if (response.ok) {
			const data = await response.json();
			return { success: true, data: data.data || [] };
		}
		let txt = "";
		try { txt = await response.text(); } catch (_) {}
		console.error("getLastExerciseSeries failed:", response.status, txt);
		return { success: false, error: `HTTP ${response.status}` };
	} catch (e) {
		console.error("getLastExerciseSeries exception:", e);
		return { success: false, error: e.message || "Erreur réseau" };
	}
};

export const getWeeklyReport = async (weekOffset = 1) => {
	try {
		const token = getToken();
		if (!token) return { success: false, error: "Non connecté" };
		const response = await apiRequest("/coach/weekly-report", {
			method: "POST",
			body: JSON.stringify({ Token: token, WeekOffset: weekOffset }),
		});
		if (response.ok) {
			const data = await response.json();
			return { success: true, data };
		}
		let errorText = "";
		try { errorText = await response.text(); } catch (_) {}
		console.error("getWeeklyReport failed:", response.status, errorText);
		return { success: false, error: `HTTP ${response.status}`, status: response.status };
	} catch (e) {
		console.error("getWeeklyReport exception:", e);
		return { success: false, error: e.message || "Erreur réseau" };
	}
};

/**
 * Get recent check-ins for a student
 * @param {number} studentId - Student ID
 * @param {number} weekCount - Number of weeks to look back
 * @returns {Promise<{success: boolean, checkins: Array}>}
 */
export const requestCheckin = async (studentId) => {
	try {
		const token = getToken();
		if (!token) return { success: false, message: "Session expirée" };
		const response = await apiRequest("/coach/request-checkin", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId }),
		});
		let body = null;
		try { body = await response.json(); } catch (_) {}
		if (!response.ok) {
			if (response.status === 401) return { success: false, message: "Session expirée" };
			if (response.status === 403) return { success: false, message: body?.message || "Abonnement payant requis" };
			if (response.status === 400) return { success: false, message: typeof body === "string" ? body : (body?.message || "Élève non lié à ton compte") };
			return { success: false, message: body?.message || "Erreur serveur" };
		}
		if (body && body.success === false) {
			return { success: false, message: body.message || "L'élève n'a pas activé les notifications" };
		}
		return { success: true };
	} catch (e) {
		return { success: false, message: "Erreur réseau" };
	}
};

export const getCoachStudentCheckins = async (studentId, weekCount = 4) => {
	try {
		const token = getToken();
		if (!token) return { success: false, checkins: [] };
		const response = await apiRequest("/coach/checkins", {
			method: "POST",
			body: JSON.stringify({ Token: token, StudentId: studentId, WeekCount: weekCount }),
		});
		if (response.ok) {
			const data = await response.json();
			return { success: true, checkins: data.checkins || [] };
		}
		return { success: false, checkins: [] };
	} catch (e) {
		return { success: false, checkins: [] };
	}
};

/**
 * Get template folders and associations for the coach
 */
export const getTemplateFolders = async () => {
	try {
		const token = getToken();
		if (!token) return { success: false, folders: [], associations: [] };

		const response = await apiRequest("/coach/template-folders", {
			method: "POST",
			body: JSON.stringify({ Token: token }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, folders: data.folders || [], associations: data.associations || [] };
		}
		return { success: false, folders: [], associations: [] };
	} catch (error) {
		console.error("Get template folders error:", error);
		return { success: false, folders: [], associations: [] };
	}
};

/**
 * Create or update a template folder
 */
export const saveTemplateFolder = async (key, nomDossier) => {
	try {
		const token = getToken();
		if (!token) return { success: false };

		const response = await apiRequest("/coach/template-folder/save", {
			method: "POST",
			body: JSON.stringify({ Token: token, Key: key, NomDossier: nomDossier }),
		});

		if (response.ok) {
			const data = await response.json();
			return { success: true, folder: data.folder };
		}
		return { success: false };
	} catch (error) {
		console.error("Save template folder error:", error);
		return { success: false };
	}
};

/**
 * Delete a template folder
 */
export const deleteTemplateFolder = async (key) => {
	try {
		const token = getToken();
		if (!token) return { success: false };

		const response = await apiRequest("/coach/template-folder/delete", {
			method: "POST",
			body: JSON.stringify({ Token: token, Key: key }),
		});

		if (response.ok) return { success: true };
		return { success: false };
	} catch (error) {
		console.error("Delete template folder error:", error);
		return { success: false };
	}
};

/**
 * Assign or unassign a template to/from a folder
 */
export const assignTemplateToFolder = async (folderKey, templateKey, assign) => {
	try {
		const token = getToken();
		if (!token) return { success: false };

		const response = await apiRequest("/coach/template-folder/assign", {
			method: "POST",
			body: JSON.stringify({ Token: token, FolderKey: folderKey, TemplateKey: templateKey, Assign: assign }),
		});

		if (response.ok) return { success: true };
		return { success: false };
	} catch (error) {
		console.error("Assign template to folder error:", error);
		return { success: false };
	}
};
